import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Save, Globe, Phone, Image as ImageIcon, MessageSquare, Megaphone, FileText, Menu as MenuIcon, Search, Tags, Sparkles } from 'lucide-react';
import { SiteConfigAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import ImageUpload from './ImageUpload';
import RichTextEditor from './RichTextEditor';
import FeaturedProductsPicker from './FeaturedProductsPicker';

// Champs gérés (en plus on accepte n'importe quelle autre clé entrée par l'admin)
const FIELDS = {
  contact: [
    { key: 'site.title', label: 'Nom du site', placeholder: 'Comptoir-Online' },
    { key: 'site.tagline', label: 'Slogan / sous-titre', placeholder: 'Comparateur dentaire' },
    { key: 'site.contact.phone', label: 'Téléphone principal', placeholder: '+213 21 00 00 00' },
    { key: 'site.contact.phone_2', label: 'Téléphone secondaire', placeholder: '(optionnel)' },
    { key: 'site.contact.email', label: 'Email principal', placeholder: 'contact@comptoir-online.com' },
    { key: 'site.contact.email_2', label: 'Email secondaire', placeholder: '(optionnel)' },
    { key: 'site.contact.address', label: 'Adresse postale', placeholder: 'Cité des affaires, Alger', wide: true },
    { key: 'site.social.facebook', label: 'URL Facebook', placeholder: 'https://facebook.com/...' },
    { key: 'site.social.instagram', label: 'URL Instagram', placeholder: 'https://instagram.com/...' },
    { key: 'site.social.linkedin', label: 'URL LinkedIn', placeholder: 'https://linkedin.com/...' },
  ],
  hero: [
    { key: 'home.hero.title', label: 'Titre principal', wide: true, placeholder: 'L\'achat dentaire enfin transparent' },
    { key: 'home.hero.subtitle', label: 'Sous-titre', wide: true, textarea: true, placeholder: 'Comparez les prix de tous nos fournisseurs locaux...' },
    { key: 'home.hero.cta_label', label: 'Texte du bouton', placeholder: 'Comparer les prix' },
    { key: 'home.hero.cta_url', label: 'Lien du bouton', placeholder: '/' },
  ],
  banner: [
    { key: 'home.banner.text', label: 'Texte de la bannière', wide: true, placeholder: 'Livraison gratuite jusqu\'au 31 décembre' },
    { key: 'home.banner.url', label: 'Lien (optionnel)', placeholder: '/promo' },
  ],
};

const SiteSettingsTab = () => {
  const { toast } = useToast();
  const { refresh: refreshSiteConfig } = useSiteConfig();
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState('contact');

  useEffect(() => {
    SiteConfigAPI.adminGet().then(setConfig).finally(() => setLoading(false));
  }, []);

  const set = (key, value) => setConfig(c => ({ ...c, [key]: value }));

  const save = async () => {
    setBusy(true);
    try {
      await SiteConfigAPI.adminSet(config);
      await refreshSiteConfig();
      toast({ title: '✅ Paramètres sauvegardés', description: 'Les modifications sont déjà visibles sur le site.' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setBusy(false); }
  };

  // Testimonials helpers
  const testimonials = Array.isArray(config['home.testimonials']) ? config['home.testimonials'] : [];
  const addTestimonial = () => set('home.testimonials', [...testimonials, { name: '', role: '', quote: '', image: '' }]);
  const updateTestimonial = (idx, key, value) => set('home.testimonials', testimonials.map((t, i) => i === idx ? { ...t, [key]: value } : t));
  const removeTestimonial = (idx) => set('home.testimonials', testimonials.filter((_, i) => i !== idx));

  if (loading) return <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...</div>;

  const sections = [
    { id: 'contact', label: 'Identité & contact', icon: Phone },
    { id: 'hero', label: 'Bandeau d\'accueil', icon: Globe },
    { id: 'featured', label: 'Produits à la une', icon: Sparkles },
    { id: 'banner', label: 'Bannière promo', icon: Megaphone },
    { id: 'testimonials', label: 'Témoignages', icon: MessageSquare },
    { id: 'pages', label: 'Pages statiques', icon: FileText },
    { id: 'menu', label: 'Menu navigation', icon: MenuIcon },
    { id: 'seo', label: 'SEO', icon: Search },
    { id: 'brands', label: 'Ordre des marques', icon: Tags },
  ];

  const renderField = (f) => (
    <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
      <label className="text-sm text-[#2D2A26] mb-1.5 block">{f.label}</label>
      {f.textarea ? (
        <Textarea
          rows={3}
          value={config[f.key] || ''}
          onChange={(e) => set(f.key, e.target.value)}
          placeholder={f.placeholder}
          data-testid={`site-setting-${f.key}`}
        />
      ) : (
        <Input
          value={config[f.key] || ''}
          onChange={(e) => set(f.key, e.target.value)}
          placeholder={f.placeholder}
          data-testid={`site-setting-${f.key}`}
        />
      )}
    </div>
  );

  return (
    <div>
      <div className="flex gap-2 mb-5 border-b border-[#E8E0D0] overflow-x-auto">
        {sections.map((s) => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setSection(s.id)}
              data-testid={`site-section-${s.id}`}
              className={`px-3 py-2 -mb-px border-b-2 text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${section === s.id ? 'border-[#1F4D3F] text-[#1F4D3F]' : 'border-transparent text-[#7A6F5C]'}`}>
              <Icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          );
        })}
      </div>

      {section === 'contact' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
          <p className="text-sm text-[#7A6F5C] mb-4">Ces informations apparaissent dans le footer et le bandeau de contact. Toute modification est instantanée.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{FIELDS.contact.map(renderField)}</div>
        </div>
      )}

      {section === 'hero' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
          <p className="text-sm text-[#7A6F5C] mb-4">Le grand bloc vert au sommet de votre page d'accueil. Choisissez aussi une image d'arrière-plan optionnelle.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">{FIELDS.hero.map(renderField)}</div>
          <ImageUpload value={config['home.hero.image']} onChange={(url) => set('home.hero.image', url)} label="Image d'arrière-plan (optionnelle — affichée à 25% d'opacité)" testId="hero-bg-image" />
        </div>
      )}

      {section === 'featured' && (
        <FeaturedProductsPicker
          value={config['home.featured_product_ids']}
          onChange={(arr) => set('home.featured_product_ids', arr)}
        />
      )}

      {section === 'banner' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-[#2D2A26]">Bannière promotionnelle</h3>
              <p className="text-xs text-[#7A6F5C] mt-1">Petit bandeau coloré qui s'affiche en haut de toutes les pages.</p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!config['home.banner.enabled']}
                onChange={(e) => set('home.banner.enabled', e.target.checked)}
                className="w-4 h-4 rounded text-[#1F4D3F]"
                data-testid="site-setting-home.banner.enabled"
              />
              Activée
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{FIELDS.banner.map(renderField)}</div>
          {config['home.banner.enabled'] && config['home.banner.text'] && (
            <div className="mt-4 bg-[#1F4D3F] text-white text-sm px-4 py-2 rounded-lg text-center">
              <strong>Aperçu :</strong> {config['home.banner.text']}
            </div>
          )}
        </div>
      )}

      {section === 'testimonials' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#7A6F5C]">Témoignages affichés sur la page d'accueil. Recommandé : 3 à 6 témoignages.</p>
            <Button size="sm" onClick={addTestimonial} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="add-testimonial">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
          {testimonials.length === 0 ? (
            <div className="text-center text-[#7A6F5C] py-6 text-sm">Aucun témoignage pour le moment.</div>
          ) : (
            <div className="space-y-4">
              {testimonials.map((t, idx) => (
                <div key={idx} className="border border-[#E8E0D0] rounded-xl p-4 bg-[#FAF7F2]" data-testid={`testimonial-row-${idx}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="text-sm text-[#7A6F5C]">Témoignage #{idx + 1}</div>
                    <button onClick={() => removeTestimonial(idx)} className="text-rose-600 hover:bg-rose-50 rounded p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input value={t.name || ''} onChange={(e) => updateTestimonial(idx, 'name', e.target.value)} placeholder="Nom (ex: Dr Karim B.)" />
                    <Input value={t.role || ''} onChange={(e) => updateTestimonial(idx, 'role', e.target.value)} placeholder="Rôle / ville (ex: Dentiste à Alger)" />
                    <Textarea rows={3} value={t.quote || ''} onChange={(e) => updateTestimonial(idx, 'quote', e.target.value)} placeholder="« Comptoir-Online me fait gagner du temps et de l'argent... »" className="sm:col-span-2" />
                  </div>
                  <div className="mt-3">
                    <ImageUpload value={t.image} onChange={(url) => updateTestimonial(idx, 'image', url)} label="Photo (optionnelle)" testId={`testimonial-image-${idx}`} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section === 'pages' && (
        <PagesEditor config={config} set={set} />
      )}

      {section === 'menu' && (
        <MenuEditor config={config} set={set} />
      )}

      {section === 'seo' && (
        <SeoEditor config={config} set={set} />
      )}

      {section === 'brands' && (
        <BrandsOrderEditor config={config} set={set} />
      )}

      <div className="mt-6 flex justify-end sticky bottom-4 z-10">
        <Button onClick={save} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white shadow-lg" data-testid="site-settings-save">
          <Save className="w-4 h-4 mr-2" /> {busy ? 'Sauvegarde...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
};

// ============ Sub-editors ============

const PAGE_LIST = [
  { key: 'about',       label: 'À propos',              route: '/a-propos' },
  { key: 'how',         label: 'Comment ça marche',     route: '/comment-ca-marche' },
  { key: 'contact',     label: 'Contact',               route: '/contact' },
  { key: 'faq',         label: 'FAQ',                   route: '/faq' },
  { key: 'shipping',    label: 'Livraison',             route: '/livraison' },
  { key: 'terms',       label: 'CGV',                   route: '/cgv' },
  { key: 'legal',       label: 'Mentions légales',      route: '/mentions-legales' },
  { key: 'privacy',     label: 'Confidentialité',       route: '/confidentialite' },
  { key: 'cookies',     label: 'Cookies',               route: '/cookies' },
];

const PagesEditor = ({ config, set }) => {
  const [selected, setSelected] = useState(PAGE_LIST[0].key);
  const titleKey = `pages.${selected}.title`;
  const bodyKey = `pages.${selected}.body_html`;
  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
      <p className="text-sm text-[#7A6F5C] mb-4">
        Modifiez le titre et le contenu des pages statiques. Utilisez la barre d'outils pour mettre en forme (gras, listes, titres, liens, images).
      </p>
      <div className="grid grid-cols-[180px_1fr] gap-4">
        <div className="space-y-1">
          {PAGE_LIST.map((p) => (
            <button key={p.key} onClick={() => setSelected(p.key)}
              data-testid={`pages-tab-${p.key}`}
              className={`w-full text-left px-3 py-2 rounded text-sm ${selected === p.key ? 'bg-[#1F4D3F] text-white' : 'hover:bg-[#FAF7F2] text-[#2D2A26]'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="space-y-3 min-w-0">
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Titre de la page</label>
            <Input
              value={config[titleKey] || ''}
              onChange={(e) => set(titleKey, e.target.value)}
              placeholder={PAGE_LIST.find(p => p.key === selected)?.label}
              data-testid={`page-title-${selected}`}
            />
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Contenu de la page</label>
            <RichTextEditor
              value={config[bodyKey] || ''}
              onChange={(html) => set(bodyKey, html)}
              placeholder="Tapez le contenu de la page…"
              testId={`page-body-${selected}`}
              minHeight={300}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MenuEditor = ({ config, set }) => {
  const menu = Array.isArray(config['navigation.menu']) ? config['navigation.menu'] : [];
  const add = () => set('navigation.menu', [...menu, { label: '', url: '' }]);
  const upd = (i, k, v) => set('navigation.menu', menu.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const rem = (i) => set('navigation.menu', menu.filter((_, idx) => idx !== i));
  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#7A6F5C]">Liens supplémentaires affichés dans la barre du haut (avant "Devenir fournisseur" / "Connexion").</p>
        <Button size="sm" onClick={add} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="menu-add"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>
      {menu.length === 0 ? (
        <div className="text-center text-[#7A6F5C] py-6 text-sm">Aucun lien personnalisé.</div>
      ) : (
        <div className="space-y-2">
          {menu.map((m, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_40px] gap-2" data-testid={`menu-row-${i}`}>
              <Input value={m.label || ''} onChange={(e) => upd(i, 'label', e.target.value)} placeholder="Label (ex: Promotions)" />
              <Input value={m.url || ''} onChange={(e) => upd(i, 'url', e.target.value)} placeholder="URL (ex: /promotions ou https://...)" />
              <button onClick={() => rem(i)} className="text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SEO_ROUTES = [
  { key: 'home',        label: 'Accueil',               route: '/' },
  { key: 'catalog',     label: 'Catégories',            route: '/categories' },
  { key: 'about',       label: 'À propos',              route: '/a-propos' },
  { key: 'contact',     label: 'Contact',               route: '/contact' },
  { key: 'blog',        label: 'Blog',                  route: '/blog' },
  { key: 'pricing',     label: 'Tarifs',                route: '/tarifs' },
];

const SeoEditor = ({ config, set }) => {
  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
      <p className="text-sm text-[#7A6F5C] mb-4">Titres et descriptions pour Google. Recommandé : titre 50-60 caractères, description 150-160 caractères.</p>
      <div className="space-y-4">
        {SEO_ROUTES.map((r) => (
          <div key={r.key} className="border border-[#E8E0D0] rounded-lg p-3" data-testid={`seo-row-${r.key}`}>
            <div className="text-xs uppercase tracking-wider text-[#1F4D3F] mb-2">{r.label} <span className="text-[#7A6F5C] normal-case ml-1">— {r.route}</span></div>
            <Input
              value={config[`seo.${r.key}.title`] || ''}
              onChange={(e) => set(`seo.${r.key}.title`, e.target.value)}
              placeholder="Title (ex: Comptoir-Online — Comparateur dentaire en Algérie)"
              className="mb-2"
              data-testid={`seo-title-${r.key}`}
            />
            <Textarea
              rows={2}
              value={config[`seo.${r.key}.description`] || ''}
              onChange={(e) => set(`seo.${r.key}.description`, e.target.value)}
              placeholder="Description courte affichée dans les résultats Google"
              data-testid={`seo-desc-${r.key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const BrandsOrderEditor = ({ config, set }) => {
  const order = Array.isArray(config['home.brands_order']) ? config['home.brands_order'] : [];
  const value = order.join(', ');
  const onChange = (raw) => {
    const arr = raw.split(',').map(s => s.trim()).filter(Boolean);
    set('home.brands_order', arr);
  };
  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
      <p className="text-sm text-[#7A6F5C] mb-4">Définissez l'ordre d'affichage des marques dans le carousel d'accueil. Séparez par des virgules. Les marques non listées s'affichent à la fin par ordre alphabétique.</p>
      <Textarea
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="3M ESPE, Dentsply, KaVo, Septodont, Henry Schein..."
        className="font-mono text-sm"
        data-testid="brands-order"
      />
    </div>
  );
};

export default SiteSettingsTab;
